import { Injectable, inject, signal, computed } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { EMPTY, tap, catchError } from 'rxjs';
import { GlobalService } from './global.service';
import { GlobalConfig, Features, SalesforceChatFeatures } from './global-config.model';
import { ToasterService } from '../../layout/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class GlobalStore {
  // Injected service
  private readonly globalService = inject(GlobalService);
  private readonly toasterService = inject(ToasterService);
  private readonly translate = inject(TranslateService);

  // Local state for modifications before saving
  private readonly localConfig = signal<GlobalConfig | null>(null);

  // Triggers for save operations
  private readonly saveTrigger = signal<{ config: GlobalConfig; timestamp: number } | null>(null);

  // Resource to load global config from the API
  readonly globalConfigResource = rxResource({
    stream: () => this.globalService.getGlobalConfig(),
  });

  // Computed to check if there are unsaved changes
  readonly hasUnsavedChanges = computed(() => this.localConfig() !== null);

  readonly saveResource = rxResource({
    params: () => this.saveTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.globalService.updateGlobalConfig(params.config).pipe(
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('GLOBAL.SAVE_SUCCESS'));
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('GLOBAL.SAVE_ERROR'));
          });
          throw error;
        })
      );
    },
  });

  // Publicly exposed signals
  getCurrentConfig(): GlobalConfig | null {
    return this.localConfig() ?? this.globalConfigResource.value() ?? null;
  }

  readonly isSaving = computed(() => this.saveResource.isLoading());
  readonly saveError = computed(() => this.saveResource.error());
  readonly saveSuccess = computed(() => {
    if (this.saveResource.value()) {
      queueMicrotask(() => this.resetLocalState());
      return true;
    }
    return false;
  });

  // Mutations
  updateDeliveriesRefresh(value: number): void {
    this.updateLocalConfig({ deliveriesRefreshEvery: value });
  }

  updateMaximumDaysForEval(value: number): void {
    this.updateLocalConfig({ maximumDaysForEval: value });
  }

  updateMaximumCoForUcActivate(value: number): void {
    this.updateLocalConfig({ maximumCoForUcActivate: value });
  }

  updateTicketDisplayTime(value: string): void {
    this.updateLocalConfig({ ticketDisplayTimeResolved: value });
  }

  updateFeature(feature: keyof Features, enabled: boolean): void {
    const current = this.getCurrentConfig();
    if (current) {
      this.localConfig.set({
        ...current,
        features: {
          ...current.features,
          [feature]: enabled,
        },
      });
    }
  }

  updateSalesforceChatFeature(ticketType: keyof SalesforceChatFeatures, enabled: boolean): void {
    const current = this.getCurrentConfig();
    if (current) {
      this.localConfig.set({
        ...current,
        features: {
          ...current.features,
          salesforceChat: {
            ...current.features.salesforceChat,
            [ticketType]: enabled,
          },
        },
      });
    }
  }

  updateFunctionTitles(titles: string[]): void {
    this.updateLocalConfig({ functionTitles: titles });
  }

  saveConfig(): void {
    // Ne sauvegarder que s'il y a des modifications
    if (!this.hasUnsavedChanges()) {
      queueMicrotask(() => {
        this.toasterService.info(this.translate.instant('GLOBAL.NO_CHANGES'));
      });
      return;
    }

    const config = this.getCurrentConfig();
    if (config) {
      this.saveTrigger.set({ config, timestamp: Date.now() });
    }
  }

  private updateLocalConfig(updates: Partial<GlobalConfig>): void {
    const current = this.getCurrentConfig();
    if (current) {
      this.localConfig.set({ ...current, ...updates });
    }
  }

  private resetLocalState(): void {
    this.localConfig.set(null);
    this.saveTrigger.set(null);
    this.globalConfigResource.reload();
  }
}
